import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Banknote, Calendar, TrendingUp, Filter, Download, 
  CreditCard, Building2, Wallet, User, RefreshCw,
  ArrowUpRight, ArrowDownRight, ChevronRight
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import useConfig, { FALLBACK_PAYMENT_METHODS, FALLBACK_PAYMENT_TYPES } from '../hooks/useConfig';
import { GuestLink, ReservationLink, SaleLink } from '../components/crm/EntityLinks';
import { formatCurrency, safeFixed } from '../utils/formatters';

// Icon mapping for payment methods
const METHOD_ICONS = {
  'CASH': Banknote,
  'CARD': CreditCard,
  'TRANSFER': ArrowUpRight,
  'CHECK': Banknote,
  'PLATFORM': Building2,
  'TOURIST_TAX': Banknote,
  'OTHER': Banknote
};

// Default flux labels (will be overridden by config)
const defaultFluxLabels = {
  'SOCIETE': { label: 'Société', icon: Building2, color: 'bg-blue-500' },
  'COMPTE_PERSO': { label: 'Compte Personnel', icon: User, color: 'bg-purple-500' },
  'CASH_CAISSE': { label: 'Caisse', icon: Wallet, color: 'bg-green-500' }
};

const sourceTypeLabels = {
  'payment': { label: 'Réservation directe', color: 'bg-purple-100 text-purple-700' },
  'payout': { label: 'Versement plateforme', color: 'bg-blue-100 text-blue-700' },
  'cash': { label: 'Mouvement caisse', color: 'bg-green-100 text-green-700' }
};

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ by_flux: [], by_method: [], by_type: [], grand_total: [], flux_options: [] });
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  
  // Load configuration dynamically
  const { options: configOptions, loading: configLoading } = useConfig(['PAYMENT_METHOD', 'PAYMENT_TYPE', 'FLUX_TYPE']);
  
  // Build dynamic maps - stabilize with specific config arrays
  const methodLabels = useMemo(() => {
    const map = {
      'TOURIST_TAX': { label: 'Taxe séjour', icon: Banknote },
      'PLATFORM': { label: 'Plateforme', icon: Building2 },
      'OTHER': { label: 'Autre', icon: Banknote }
    };
    if (!configLoading && configOptions.PAYMENT_METHOD?.length > 0) {
      configOptions.PAYMENT_METHOD.forEach(o => {
        map[o.code] = { label: o.label, icon: METHOD_ICONS[o.code] || Banknote };
      });
      return map;
    }
    return {
      'CASH': { label: 'Espèces', icon: Banknote },
      'CARD': { label: 'Carte bancaire', icon: CreditCard },
      'TRANSFER': { label: 'Virement', icon: ArrowUpRight },
      'CHECK': { label: 'Chèque', icon: Banknote },
      'PLATFORM': { label: 'Plateforme', icon: Building2 },
      'TOURIST_TAX': { label: 'Taxe séjour', icon: Banknote },
      'OTHER': { label: 'Autre', icon: Banknote }
    };
  }, [configLoading, configOptions.PAYMENT_METHOD]);
  
  const paymentTypeLabels = useMemo(() => {
    if (!configLoading && configOptions.PAYMENT_TYPE?.length > 0) {
      const map = {};
      configOptions.PAYMENT_TYPE.forEach(o => {
        const colorClass = o.code === 'DEPOSIT' ? 'text-amber-600 bg-amber-50' : 
                          o.code === 'BALANCE' ? 'text-blue-600 bg-blue-50' : 
                          'text-green-600 bg-green-50';
        map[o.code] = { label: o.label, color: colorClass };
      });
      return map;
    }
    return {
      'DEPOSIT': { label: 'Acompte', color: 'text-amber-600 bg-amber-50' },
      'BALANCE': { label: 'Solde', color: 'text-blue-600 bg-blue-50' },
      'FULL': { label: 'Paiement total', color: 'text-green-600 bg-green-50' }
    };
  }, [configLoading, configOptions.PAYMENT_TYPE]);
  
  // Dynamic flux labels from config
  const [fluxLabels, setFluxLabels] = useState(defaultFluxLabels);
  
  // Filters
  const [filters, setFilters] = useState({
    type_flux: '',
    method: '',
    payment_type: '',
    source_type: '',
    start_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end_date: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.type_flux) params.append('type_flux', filters.type_flux);
      if (filters.method) params.append('method', filters.method);
      if (filters.payment_type) params.append('payment_type', filters.payment_type);
      if (filters.source_type) params.append('source_type', filters.source_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('page', pagination.page);
      params.append('limit', 50);

      const response = await api.get(`/payments?${params.toString()}`);
      setPayments(response.data.data || []);
      setPagination(response.data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      toast.error('Erreur lors du chargement des encaissements');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await api.get(`/payments/summary?${params.toString()}`);
      setSummary(response.data);
      
      // Build dynamic flux labels from config
      if (response.data.flux_options?.length > 0) {
        const dynamicLabels = {};
        response.data.flux_options.forEach(flux => {
          dynamicLabels[flux.code] = {
            label: flux.label,
            color: flux.color ? `bg-[${flux.color}]` : 'bg-gray-500',
            colorHex: flux.color
          };
        });
        setFluxLabels({ ...defaultFluxLabels, ...dynamicLabels });
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchSummary();
  }, [filters, pagination.page]);

  // Quick date filters
  const setDateRange = (range) => {
    const today = new Date();
    let start, end;
    
    switch (range) {
      case 'today':
        start = end = format(today, 'yyyy-MM-dd');
        break;
      case 'week':
        start = format(subDays(today, 7), 'yyyy-MM-dd');
        end = format(today, 'yyyy-MM-dd');
        break;
      case 'month':
        start = format(startOfMonth(today), 'yyyy-MM-dd');
        end = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'all':
        start = '';
        end = '';
        break;
      default:
        return;
    }
    
    setFilters({ ...filters, start_date: start, end_date: end });
  };

  const grandTotal = summary.grand_total?.reduce((sum, item) => sum + parseFloat(item.total || 0), 0) || 0;

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Banknote className="w-6 h-6 text-green-600" />
            Encaissements
          </h1>
          <p className="text-muted-foreground">Suivi des entrées d'argent par flux</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchPayments(); fetchSummary(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Summary Cards by Flux - Dynamic from config */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* Grand Total */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Total Encaissé</p>
                <p className="text-3xl font-bold text-green-800">{formatCurrency(grandTotal)}</p>
                <p className="text-xs text-green-600 mt-1">
                  {summary.grand_total?.[0]?.count || 0} encaissement(s)
                </p>
              </div>
              <div className="p-3 bg-green-500 rounded-full">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* By Flux - Dynamic from summary */}
        {summary.by_flux?.map((flux) => {
          const config = fluxLabels[flux.type_flux] || { label: flux.label || flux.type_flux, color: 'bg-gray-500' };
          const FluxIcon = config.icon || Banknote;
          
          return (
            <Card 
              key={flux.type_flux} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filters.type_flux === flux.type_flux ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setFilters({ ...filters, type_flux: filters.type_flux === flux.type_flux ? '' : flux.type_flux })}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{flux.label || config.label}</p>
                    <p className="text-2xl font-bold">{formatCurrency(flux.total || 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{flux.count || 0} encaissement(s)</p>
                  </div>
                  <div 
                    className="p-3 rounded-full"
                    style={{ backgroundColor: flux.color || config.colorHex || '#6B7280' }}
                  >
                    <FluxIcon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Quick Date Filters */}
            <div className="flex gap-1">
              <Button 
                variant={filters.start_date === format(new Date(), 'yyyy-MM-dd') ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setDateRange('today')}
              >
                Aujourd'hui
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDateRange('week')}
              >
                7 jours
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDateRange('month')}
              >
                Ce mois
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDateRange('all')}
              >
                Tout
              </Button>
            </div>

            <div className="h-6 w-px bg-border" />

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <Label className="text-sm">Du</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-[140px]"
              />
              <Label className="text-sm">au</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-[140px]"
              />
            </div>

            <div className="h-6 w-px bg-border" />

            {/* Method Filter */}
            <Select value={filters.method || 'all'} onValueChange={(v) => setFilters({ ...filters, method: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les modes</SelectItem>
                {Object.entries(methodLabels).map(([value, config]) => (
                  <SelectItem key={value} value={value}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Payment Type Filter */}
            <Select value={filters.payment_type || 'all'} onValueChange={(v) => setFilters({ ...filters, payment_type: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {Object.entries(paymentTypeLabels).map(([value, config]) => (
                  <SelectItem key={value} value={value}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Source Type Filter */}
            <Select value={filters.source_type || 'all'} onValueChange={(v) => setFilters({ ...filters, source_type: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sources</SelectItem>
                {Object.entries(sourceTypeLabels).map(([value, config]) => (
                  <SelectItem key={value} value={value}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {(filters.type_flux || filters.method || filters.payment_type || filters.source_type) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setFilters({ ...filters, type_flux: '', method: '', payment_type: '', source_type: '' })}
              >
                Effacer filtres
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Détail des encaissements</span>
            <Badge variant="secondary">{pagination.total} résultat(s)</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Banknote className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun encaissement trouvé pour cette période</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Source</th>
                    <th className="pb-3 font-medium">Flux</th>
                    <th className="pb-3 font-medium">Référence / Client</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Mode</th>
                    <th className="pb-3 font-medium text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const fluxConfig = summary.by_flux?.find(f => f.type_flux === payment.type_flux) || fluxLabels[payment.type_flux] || { label: payment.type_flux, color: '#6B7280' };
                    const methodConfig = methodLabels[payment.method] || { label: payment.method };
                    const typeConfig = paymentTypeLabels[payment.payment_type] || { label: payment.payment_type || '-', color: 'text-gray-600 bg-gray-50' };
                    const sourceConfig = sourceTypeLabels[payment.source_type] || { label: payment.source_type, color: 'bg-gray-100 text-gray-700' };
                    const MethodIcon = methodConfig.icon || Banknote;
                    
                    // Extract entity info for CRM links
                    const hasReservation = payment.reservation_id || payment.reservation;
                    const hasSale = payment.sale || payment.raw?.reference_type === 'Sale';
                    const saleData = payment.sale || (hasSale ? { id: payment.raw?.reference_id } : null);
                    const reservationData = payment.reservation || (hasReservation ? { id: payment.reservation_id } : null);
                    
                    // Get guest info if available
                    const guestInfo = payment.client ? {
                      id: payment.raw?.reservation?.guest?.id || payment.raw?.Reservation?.guest?.id,
                      first_name: payment.client.split(' ')[0],
                      last_name: payment.client.split(' ').slice(1).join(' ')
                    } : null;
                    
                    return (
                      <tr 
                        key={payment.id} 
                        className="border-b hover:bg-muted/50"
                      >
                        <td className="py-3">
                          <p className="font-medium">{format(new Date(payment.date), 'dd/MM/yyyy')}</p>
                          <p className="text-xs text-muted-foreground">{format(new Date(payment.date), 'HH:mm')}</p>
                        </td>
                        <td className="py-3">
                          <Badge variant="outline" className={sourceConfig.color}>
                            {sourceConfig.label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <Badge 
                            style={{ backgroundColor: fluxConfig.color || fluxConfig.colorHex || '#6B7280' }}
                            className="text-white"
                          >
                            {fluxConfig.label || payment.type_flux}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="space-y-1">
                            {/* Client name (as text if no link, or as link if guest exists) */}
                            {payment.client ? (
                              <p className="font-medium">{payment.client}</p>
                            ) : (
                              <p className="font-medium text-muted-foreground">{payment.description || '-'}</p>
                            )}
                            {/* Reference link - Reservation or Sale */}
                            <div className="text-xs">
                              {hasSale && saleData?.id ? (
                                <SaleLink 
                                  sale={{ id: saleData.id, sale_number: saleData.sale_number || `V-${saleData.id}` }} 
                                  showIcon={true}
                                  className="text-xs"
                                />
                              ) : hasReservation && reservationData?.id ? (
                                <ReservationLink 
                                  reservation={{ 
                                    id: reservationData.id, 
                                    reservation_number: reservationData.reservation_number || payment.raw?.reservation?.reservation_number
                                  }} 
                                  showIcon={true}
                                  className="text-xs"
                                />
                              ) : payment.reference ? (
                                <span className="text-muted-foreground">{payment.reference}</span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant="outline" className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <MethodIcon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{methodConfig.label}</span>
                          </div>
                        </td>
                        <td className="py-3 text-right">
                          <div>
                            <p className="font-bold text-green-600">
                              +{formatCurrency(payment.amount, 'MAD')}
                            </p>
                            {payment.currency_original && payment.currency_original !== 'MAD' && (
                              <p className="text-xs text-muted-foreground">
                                ({payment.amount_original} {payment.currency_original} × {safeFixed(payment.exchange_rate, 4, '?')})
                              </p>
                            )}
                          </div>
                          {payment.platform && (
                            <p className="text-xs text-muted-foreground">{payment.platform}</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              >
                Précédent
              </Button>
              <span className="flex items-center px-3 text-sm text-muted-foreground">
                Page {pagination.page} sur {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              >
                Suivant
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary by Method & Type */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* By Method */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Par mode de paiement</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.by_method?.length > 0 ? (
              <div className="space-y-3">
                {summary.by_method.map((item, idx) => {
                  const config = methodLabels[item.method] || { label: item.method };
                  const Icon = config.icon || Banknote;
                  return (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{config.label}</p>
                          <p className="text-xs text-muted-foreground">{item.count} paiement(s)</p>
                        </div>
                      </div>
                      <p className="font-bold">{formatCurrency(item.total, 'MAD')}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>

        {/* By Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Par type de paiement</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.by_type?.length > 0 ? (
              <div className="space-y-3">
                {summary.by_type.map((item, idx) => {
                  const config = paymentTypeLabels[item.payment_type] || { label: item.payment_type, color: '' };
                  return (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={config.color}>{config.label}</Badge>
                        <p className="text-xs text-muted-foreground">{item.count} paiement(s)</p>
                      </div>
                      <p className="font-bold">{formatCurrency(item.total, 'MAD')}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
